import { DEFAULT_CATEGORY_RECIPIENTS, getDynamicAnswerKey, getDynamicAnswerValue } from '@/utils/utils';

// TRM 6: a contact is entered once and reused across roles through the "Same as [name]" options.

// The phone input is masked, so an emptied field can hold only mask characters
const hasContactValue = (value: any) => (typeof value === 'string' ? value.replace(/[_\-\s()]/g, '') !== '' : !!value);
export interface ContactInfo {
  name?: string;
  relationship?: string;
  phone?: string;
  email?: string;
  contactMethod?: string;
}

// Where each Step 4 role stores its contact (follow-up id prefix) and which answer means "a new person entered here"
export const CONTACT_ROLES: Record<string, { prefix: string; newContactKey: string; hasEmail: boolean; contactMethods?: string[] }> = {
  medicalDecisionMaker: { prefix: 'medical_poa', newContactKey: 'someone_else', hasEmail: true },
  financialDecisionMaker: { prefix: 'financial_poa', newContactKey: 'someone_else', hasEmail: true },
  // HP-3: no "No direct communication" for the coordinator; the emergency contact has no email and uses "Emergencies only"
  primaryCoordinator: { prefix: 'coordinator', newContactKey: 'no', hasEmail: true, contactMethods: ['phone', 'text', 'email', 'both'] },
  primaryEmergencyContact: { prefix: 'em', newContactKey: 'someone_else', hasEmail: false, contactMethods: ['phone', 'text', 'both', 'emergencies_only'] },
};

// "Same as" answer → the role whose contact is reused (null = the Step 1 Decision-Maker)
export const SAME_AS_SOURCES: Record<string, string | null> = {
  yes_decision_maker: null,
  yes_medical_poa: 'medicalDecisionMaker',
  same_representative: 'medicalDecisionMaker',
  yes_financial_poa: 'financialDecisionMaker',
  yes_coordinator: 'primaryCoordinator',
};

export const isSameAsKey = (key: string) => Object.prototype.hasOwnProperty.call(SAME_AS_SOURCES, key);

// The person completing the intake is the Decision-Maker when they answer Yes to "Are you involved in making care
// decisions?" or, on the "myself" route, Yes to isRecipientDecisionMaker (the care recipient decides for themselves).
// Each route stores that person under its own prefix (callerName, hasCallerEmail → callerEmail, caller_contact_pref…)
const INQUIRER_PREFIXES: Record<string, string> = { '1': 'caller', '3': 'filler', '4': 'referral' };
const SELF_ROUTE = '2';
// The intake's relationship answer → the roles' relationship choices; others (care manager, planner…) are left to the role
const INQUIRER_RELATIONSHIPS: Record<string, string> = {
  spouse_partner: 'spouse',
  adult_child: 'adult_child',
  other_family_member: 'other_family_member',
  friend_neighbor: 'friend',
  power_of_attorney: 'poa',
};

const getRoute = (values: Record<string, any>) => `${getDynamicAnswerValue(values, 'isStaff', DEFAULT_CATEGORY_RECIPIENTS) ?? ''}`;

const isInquirerDecisionMaker = (values: Record<string, any>) => {
  const get = (id: string) => getDynamicAnswerValue(values, id, DEFAULT_CATEGORY_RECIPIENTS);
  return getRoute(values) === SELF_ROUTE ? get('isRecipientDecisionMaker') === 'yes' : get('decisionMaker') === 'yes';
};

const getInquirerContact = (values: Record<string, any>): ContactInfo => {
  const get = (id: string) => getDynamicAnswerValue(values, id, DEFAULT_CATEGORY_RECIPIENTS);
  const route = getRoute(values);
  if (route === SELF_ROUTE) {
    const contactMethod = get('recipientPreferredContactMethod');
    return {
      name: get('recipientName'),
      phone: get('recipientPhoneNumber'),
      email: get('hasRecipientEmail') === 'yes' ? get('recipientEmail') : undefined,
      // the care recipient's "No direct communication" has another key than the roles' one
      contactMethod: contactMethod === 'no_direct_communication' ? 'no_communication' : contactMethod,
    };
  }
  // the route decides which person's answers apply (another route's answers may still be in the form store)
  const prefix = INQUIRER_PREFIXES[route];
  if (!prefix) return {};
  const hasEmail = get(`has${prefix[0].toUpperCase()}${prefix.slice(1)}Email`) === 'yes';
  return {
    name: get(`${prefix}Name`),
    relationship: INQUIRER_RELATIONSHIPS[get('relationship')],
    phone: get(`${prefix}PhoneNumber`),
    email: hasEmail ? get(`${prefix}Email`) : undefined,
    contactMethod: get(hasEmail ? `${prefix}_contact_pref_with_email` : `${prefix}_contact_pref`),
  };
};

export const getSourceContact = (sameAsKey: string, values: Record<string, any>, recipient: string): ContactInfo => {
  if (sameAsKey === 'yes_decision_maker') {
    // Yes: the person completing the intake; otherwise the Decision-Maker entered in the follow-ups
    if (isInquirerDecisionMaker(values)) return getInquirerContact(values);
    // The Step 1 Decision-Maker is asked once, for the 1st recipient
    const get = (id: string) => getDynamicAnswerValue(values, id, DEFAULT_CATEGORY_RECIPIENTS);
    return {
      name: get('decision_maker_name'),
      relationship: get('relationship_to_care_recipient'),
      phone: get('contact_phone'),
      email: get('email_address'),
      contactMethod: get('decision_maker_contact_method'),
    };
  }
  const sourceRole = SAME_AS_SOURCES[sameAsKey];
  if (!sourceRole) return {};
  const { prefix, newContactKey } = CONTACT_ROLES[sourceRole];
  // The form store keeps follow-ups of a previous choice, so only read them while the source role still holds a contact
  const sourceAnswer = getDynamicAnswerValue(values, sourceRole, recipient);
  if (sourceAnswer !== newContactKey && sourceAnswer !== 'sameFirstPerson' && !isSameAsKey(sourceAnswer)) return {};
  const get = (field: string) => getDynamicAnswerValue(values, `${prefix}_${field}`, recipient);
  return {
    name: get('name'),
    relationship: get('relationship'),
    phone: get('phone'),
    email: get('email'),
    contactMethod: get('contact_method'),
  };
};

const DECISION_MAKER_FIELDS: Record<string, string> = {
  name: 'decision_maker_name',
  relationship: 'relationship_to_care_recipient',
  phone: 'contact_phone',
  email: 'email_address',
  contact_method: 'decision_maker_contact_method',
};

// Form key of one field of the source contact, so a value added on the role can be written back to that person
const getSourceFieldKey = (sameAsKey: string, field: string, values: Record<string, any>, recipient: string) => {
  const keys = Object.keys(values);
  if (sameAsKey === 'yes_decision_maker') {
    // the person completing the intake: their phone is required and their email sits behind its own Yes / No question
    if (isInquirerDecisionMaker(values)) return undefined;
    const nameKey = getDynamicAnswerKey(keys, 'decision_maker_name', DEFAULT_CATEGORY_RECIPIENTS);
    return nameKey?.replace(/decision_maker_name$/, DECISION_MAKER_FIELDS[field]);
  }
  const sourceRole = SAME_AS_SOURCES[sameAsKey];
  if (!sourceRole) return undefined;
  const roleKey = getDynamicAnswerKey(keys, sourceRole, recipient);
  return roleKey ? `${roleKey}followUpQ/${CONTACT_ROLES[sourceRole].prefix}_${field}` : undefined;
};

// Main answers of the roles currently answered "Same as [name]"
const getSameAsRoleKeys = (values: Record<string, any>) =>
  Object.keys(values).filter((answerKey) => {
    const [, , qKey, ...rest] = answerKey.split('/');
    const sameAsKey = values[answerKey];
    return !rest.length && !!CONTACT_ROLES[qKey] && typeof sameAsKey === 'string' && isSameAsKey(sameAsKey);
  });

// Roles answered "Same as [name]" mirror their source contact (one record per person, TRM 6.3 / 6.5):
// - the name is only filled when empty; a renamed source is confirmed first (see getRenamedSameAsRoles), then synced with withName
// - an empty source field never wipes what was entered on the role
// - backfill (on submit): a phone / email added on the role fills that person's empty field
export const syncSameAsContacts = (answers: Record<string, any>, options: { withName?: boolean; backfill?: boolean } = {}) => {
  const result = { ...answers };
  getSameAsRoleKeys(result).forEach((answerKey) => {
    const [recipient, , qKey] = answerKey.split('/');
    const role = CONTACT_ROLES[qKey];
    const sameAsKey = result[answerKey];
    const field = (name: string) => `${answerKey}followUpQ/${role.prefix}_${name}`;

    if (options.backfill) {
      ['phone', ...(role.hasEmail ? ['email'] : [])].forEach((name) => {
        const sourceKey = getSourceFieldKey(sameAsKey, name, result, recipient);
        if (sourceKey && !hasContactValue(result[sourceKey]) && hasContactValue(result[field(name)])) {
          result[sourceKey] = result[field(name)];
        }
      });
    }

    const source = getSourceContact(sameAsKey, result, recipient);
    if (!source.name) return;
    if (options.withName || !result[field('name')]) result[field('name')] = source.name;
    if (hasContactValue(source.relationship)) result[field('relationship')] = source.relationship;
    if (hasContactValue(source.phone)) result[field('phone')] = source.phone;
    if (role.hasEmail && hasContactValue(source.email)) result[field('email')] = source.email;
    result[field('contact_method')] = getSameAsContactMethod(role, result[field('contact_method')], source.contactMethod);
  });
  return result;
};

// Contact method of a "Same as" role: the source's method when it has one (an empty one never wipes the role's choice);
// a role with its own choices keeps its own preference: prefill only when empty and the source's method is one of them
export const getSameAsContactMethod = (role: { contactMethods?: string[] }, current?: string, sourceMethod?: string) => {
  if (!hasContactValue(sourceMethod)) return current;
  if (!role.contactMethods) return sourceMethod;
  return !current && role.contactMethods.includes(sourceMethod as string) ? sourceMethod : current;
};

// "Same as" roles whose name no longer matches their source, i.e. the source's name was edited (TRM 6.4 / 6.5)
export const getRenamedSameAsRoles = (values: Record<string, any>) =>
  getSameAsRoleKeys(values).filter((answerKey) => {
    const [recipient, , qKey] = answerKey.split('/');
    const sourceName = getSourceContact(values[answerKey], values, recipient).name?.trim();
    const roleName = values[`${answerKey}followUpQ/${CONTACT_ROLES[qKey].prefix}_name`]?.trim();
    return !!sourceName && !!roleName && sourceName !== roleName;
  });

// "Same as" roles whose source role no longer holds a person (e.g. the Medical POA changed to "No"):
// they become their own contact with that person's details, like a rename answered "different person"
export const getOrphanedSameAsRoles = (values: Record<string, any>) =>
  getSameAsRoleKeys(values).filter((answerKey) => {
    const sourceRole = SAME_AS_SOURCES[values[answerKey]];
    if (!sourceRole) return false;
    const sourceAnswer = getDynamicAnswerValue(values, sourceRole, answerKey.split('/')[0]);
    return sourceAnswer !== CONTACT_ROLES[sourceRole].newContactKey && sourceAnswer !== 'sameFirstPerson' && !isSameAsKey(sourceAnswer);
  });

// "Same as" answer a later role uses to point at an earlier role
const SAME_AS_ROLE_KEYS: Record<string, Record<string, string>> = {
  medicalDecisionMaker: { financialDecisionMaker: 'yes_medical_poa', primaryCoordinator: 'yes_medical_poa', primaryEmergencyContact: 'yes_medical_poa' },
  financialDecisionMaker: { primaryCoordinator: 'yes_financial_poa', primaryEmergencyContact: 'yes_financial_poa' },
  primaryCoordinator: { primaryEmergencyContact: 'yes_coordinator' },
};

// "Different person": the roles keep the previous person's details (TRM 6.4 / Scenario D), still one record per person:
// the first role (screen order) becomes that person's own contact, the later ones "Same as" that role
export const detachSameAsRoles = (roleKeys: string[], values: Record<string, any>) => {
  const roleOrder = Object.keys(CONTACT_ROLES);
  const getRole = (answerKey: string) => answerKey.split('/')[2];
  const holders: Record<string, string> = {};
  return [...roleKeys]
    .sort((a, b) => roleOrder.indexOf(getRole(a)) - roleOrder.indexOf(getRole(b)))
    .reduce((updates, answerKey) => {
      const role = getRole(answerKey);
      // the same previous person: same recipient, same "Same as" source
      const person = `${answerKey.split('/')[0]}|${values[answerKey]}`;
      const holderRole = holders[person];
      if (holderRole && SAME_AS_ROLE_KEYS[holderRole]?.[role]) {
        updates[answerKey] = SAME_AS_ROLE_KEYS[holderRole][role];
      } else {
        updates[answerKey] = CONTACT_ROLES[role].newContactKey;
        if (!holderRole) holders[person] = role;
      }
      return updates;
    }, {} as Record<string, any>);
};

// Follow-up fields of the 4 roles (".../primaryCoordinatorfollowUpQ/coordinator_phone")
// and of the Step 1 Decision-Maker, which a role can now write back to
export const isContactField = (key: string) =>
  Object.entries(CONTACT_ROLES).some(([qKey, { prefix }]) => key.includes(`/${qKey}followUpQ/${prefix}_`))
  || (/\/(decisionMaker|isRecipientDecisionMaker)followUpQ\//.test(key)
    && Object.values(DECISION_MAKER_FIELDS).some((id) => key.endsWith(`/${id}`)));

// Only the fields that syncing would change, so they can be pushed into the form
export const getSameAsUpdates = (values: Record<string, any>, options: { withName?: boolean } = {}) => {
  // A renamed name passes along chains (Financial "Same person as above" → Medical → Decision-Maker), so sync twice
  const synced = options.withName ? syncSameAsContacts(syncSameAsContacts(values, options), options) : syncSameAsContacts(values, options);
  const updates: Record<string, any> = {};
  Object.keys(synced).forEach((key) => {
    if (synced[key] !== values[key]) updates[key] = synced[key];
  });
  return updates;
};

// Fields copied from the source contact; they are read-only while the role is "Same as [name]"
const SYNCED_FIELDS = ['name', 'relationship', 'phone', 'email', 'contact_method'];

// The name is always locked; another field only while the source person has it,
// so a missing one (e.g. phone) can be entered on the role and is written back to that person on submit
export const isLockedContactField = (
  questionKey: string,
  choiceKey: string,
  fieldId: string,
  values: Record<string, any>,
  recipient: string,
) => {
  const role = CONTACT_ROLES[questionKey];
  if (!role || !isSameAsKey(choiceKey)) return false;
  if (role.contactMethods && fieldId === `${role.prefix}_contact_method`) return false;
  const field = SYNCED_FIELDS.find((name) => fieldId === `${role.prefix}_${name}`);
  if (!field) return false;
  if (field === 'name') return true;
  const source = getSourceContact(choiceKey, values, recipient) as Record<string, any>;
  return hasContactValue(source[field === 'contact_method' ? 'contactMethod' : field]);
};

// ===== HP-3 (Item 6 rule): contact-method choices must match the phone / email entered (every contact screen) =====
interface ContactFields {
  phone: string[];
  email: string[];
  // "Do you have … email?": "yes" means an email is given (the email itself is required there), "no" means none
  emailGate?: string;
  // the phone is always required on that screen, so its choices are always offered
  phoneRequired?: boolean;
}
export const CONTACT_METHOD_FIELDS: Record<string, ContactFields> = {
  decision_maker_contact_method: { phone: ['contact_phone'], email: ['email_address'] },
  discharge_coordinator_contact_method: { phone: ['discharge_coordinator_phone'], email: ['discharge_coordinator_email'] },
  medical_poa_contact_method: { phone: ['medical_poa_phone'], email: ['medical_poa_email'] },
  financial_poa_contact_method: { phone: ['financial_poa_phone'], email: ['financial_poa_email'] },
  coordinator_contact_method: { phone: ['coordinator_phone'], email: ['coordinator_email'] },
  em_contact_method: { phone: ['em_phone', 'em_alt_phone'], email: [] },
  caller_contact_pref: { phone: ['callerPhoneNumber'], email: ['callerEmail'], emailGate: 'hasCallerEmail', phoneRequired: true },
  recipientPreferredContactMethod: { phone: ['recipientPhoneNumber'], email: ['recipientEmail'], emailGate: 'hasRecipientEmail' },
};

// Last segment of the form name: ".../medicalDecisionMakerfollowUpQ/medical_poa_contact_method" → "medical_poa_contact_method"
export const getContactMethodFieldId = (name: string) => name.split('/').pop() || '';

// hasPhone / hasEmail: what the chosen method can rely on; isEmpty: nothing entered yet, so every choice is still offered
export const getContactAvailability = (fieldId: string, values: Record<string, any>, recipient: string, phoneRequired = false) => {
  const config = CONTACT_METHOD_FIELDS[fieldId];
  const has = (ids: string[]) => ids.some((id) => hasContactValue(getDynamicAnswerValue(values, id, recipient)));
  const phoneEntered = has(config.phone);
  const emailEntered = has(config.email);
  const emailGateValue = config.emailGate ? getDynamicAnswerValue(values, config.emailGate, recipient) : undefined;
  return {
    hasPhone: phoneRequired || !!config.phoneRequired || phoneEntered,
    hasEmail: config.emailGate ? emailGateValue === 'yes' : emailEntered,
    isEmpty: !phoneEntered && !emailEntered && !emailGateValue,
  };
};

// Phone call / Text message / Any of these need a phone; Email needs an email; "No direct communication" and "Emergencies only" always apply
export const isContactMethodAllowed = (method: string, hasPhone: boolean, hasEmail: boolean) => {
  if (['phone', 'text', 'both'].includes(method)) return hasPhone;
  if (method === 'email') return hasEmail;
  return true;
};
